import React, { forwardRef } from 'react';
import { Container as MuiContainer } from '@mui/material';
import { isEmpty } from 'lodash';

import { reportErrorInProductionOrThrow } from '~/Utils';

/**
 This Component is a wrapper on MUI component.
 This component should be imported and not MUI directly!
**/

/**
 * Explicit Container props based on actual usage in the codebase.
 */
export interface ContainerProps {
  /** Determine the max-width of the container. Set to `false` to disable `maxWidth`. */
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  /** CSS class name to apply to the root element */
  className?: string;
  /** The content of the container. `NonNullable` mirrors `@mui/material` Container's own `ContainerTypeMap` contract (`children: NonNullable<React.ReactNode>`); widening this fails `tsc` at the `MuiContainer` call site. */
  children: NonNullable<React.ReactNode>;
}

const Container = forwardRef<HTMLDivElement, ContainerProps>(({ maxWidth, className, children, ...restProps }, ref) => {
  if (!isEmpty(restProps)) {
    reportErrorInProductionOrThrow(
      `Unexpected props passed to Container: ${Object.keys(restProps).join(', ')}.
Examine if these need to be removed, or added explicitly to the ContainerProps interface and to the component's signature.`,
      true
    );
  }
  return (
    <MuiContainer maxWidth={maxWidth} className={className} ref={ref} {...restProps}>
      {children}
    </MuiContainer>
  );
});

Container.displayName = 'Container';

export default Container;
